import React, { memo } from 'react';
import { getRatingColor } from '../utils/helpers';
import { getAssignmentDisplayNumber, getAssignmentLabel } from '../utils/assignments';
import PlayerNameBadges from './PlayerNameBadges';

const lineupBtnStyle = {
  fontSize: '10px',
  padding: '2px 6px',
  borderRadius: '4px',
  border: '1px solid var(--button-bg)',
  color: 'white',
  cursor: 'pointer',
  minWidth: '68px',
  textAlign: 'center',
  backgroundColor: 'var(--button-bg)',
};

function PlayerTableRow({ player, rateColumns, rateMin, rateMax, assignmentMap, onPlayerClick, onAddToLineup, gridTemplate }) {
  const assignment = assignmentMap.get(player.id) || '';
  const assigned = Boolean(assignment);

  return (
    <div
      style={{ gridTemplateColumns: gridTemplate }}
      className={`virtual-table-row player-row ${assigned ? 'assigned' : ''}`}
      onClick={(e) => onPlayerClick(e, player)}
    >
      <div className="virtual-table-cell sticky-column player-name-cell">
        <div className="player-name-row">
          <span className="player-name-text">{player.name}</span>
          <PlayerNameBadges player={player} size="large" />
          <button
            className="wishlist-btn"
            onClick={(e) => {
              e.stopPropagation();
              onAddToLineup(player, e);
            }}
            style={lineupBtnStyle}
          >
            + Lineup
          </button>
        </div>
      </div>
      <div className="virtual-table-cell">{player.positionDisplay}</div>
      <div className="virtual-table-cell">{player.age || '-'}</div>
      <div className="virtual-table-cell">{player.team === 'N/A' ? 'FA' : player.team}</div>
      <div className="virtual-table-cell">
        ${player.salary ? Number(player.salary).toLocaleString('en-US') : '0'}
      </div>
      <div className="virtual-table-cell">
        {assigned ? (
          <div className="assigned-badge" title={getAssignmentLabel(assignment)}>
            {getAssignmentDisplayNumber(assignment) || 'L1'}
          </div>
        ) : (
          <div className="unassigned-badge" title="Click to assign">
            •
          </div>
        )}
      </div>
      {rateColumns.map((rate) => {
        const value = player[rate] || 0;
        const backgroundColor = getRatingColor(value, rateMin, rateMax);
        return (
          <div key={rate} className="virtual-table-cell rating-cell" style={{ backgroundColor }}>
            {value}
          </div>
        );
      })}
    </div>
  );
}

const MemoizedPlayerTableRow = memo(PlayerTableRow);

function VirtualizedPlayerTable({
  players,
  rateColumns,
  rateMin,
  rateMax,
  sortColumn,
  sortDirection,
  onHeaderClick,
  assignmentMap,
  onPlayerClick,
  onAddToLineup,
}) {
  const gridTemplate = `180px 80px 50px 90px 90px 70px repeat(${rateColumns.length}, 44px)`;

  return (
    <div className="players-table-container virtual-players-table">
      <div className="virtual-table-header" style={{ gridTemplateColumns: gridTemplate }}>
        <div className="virtual-table-cell sticky-column sortable-header" onClick={() => onHeaderClick('name')}>
          Name {sortColumn === 'name' && (sortDirection === 'asc' ? '↑' : '↓')}
        </div>
        <div className="virtual-table-cell sortable-header" onClick={() => onHeaderClick('positionDisplay')}>
          Pos {sortColumn === 'positionDisplay' && (sortDirection === 'asc' ? '↑' : '↓')}
        </div>
        <div className="virtual-table-cell sortable-header" onClick={() => onHeaderClick('age')}>
          Age {sortColumn === 'age' && (sortDirection === 'asc' ? '↑' : '↓')}
        </div>
        <div className="virtual-table-cell sortable-header" onClick={() => onHeaderClick('team')}>
          Team {sortColumn === 'team' && (sortDirection === 'asc' ? '↑' : '↓')}
        </div>
        <div className="virtual-table-cell sortable-header" onClick={() => onHeaderClick('salary')}>
          Salary {sortColumn === 'salary' && (sortDirection === 'asc' ? '↑' : '↓')}
        </div>
        <div className="virtual-table-cell">Status</div>
        {rateColumns.map((rate) => (
          <div
            key={rate}
            className="virtual-table-cell rating-header sortable-header"
            onClick={() => onHeaderClick(rate)}
          >
            {rate} {sortColumn === rate && (sortDirection === 'asc' ? '↑' : '↓')}
          </div>
        ))}
      </div>
      <div className="virtual-table-body">
        {players.map((player) => (
          <MemoizedPlayerTableRow
            key={player.id}
            player={player}
            rateColumns={rateColumns}
            rateMin={rateMin}
            rateMax={rateMax}
            assignmentMap={assignmentMap}
            onPlayerClick={onPlayerClick}
            onAddToLineup={onAddToLineup}
            gridTemplate={gridTemplate}
          />
        ))}
      </div>
    </div>
  );
}

export default memo(VirtualizedPlayerTable);
